import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ClipboardText, CheckCircle, Warning, Download, Upload, PaperPlaneTilt,
  MagnifyingGlass, Pencil, Lock, ArrowClockwise, FileText, XCircle, Eye, CaretDown
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_CONFIG = {
  initiated: { label: 'Initiated', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  draft_submitted: { label: 'Draft Submitted — Awaiting PM Review', color: 'bg-purple-100 text-purple-700' },
  queried: { label: 'Queried — Recount Required', color: 'bg-orange-100 text-orange-700' },
  closed: { label: 'Closed', color: 'bg-green-100 text-green-700' }
};

const VARIANCE_REASONS = ["Breakage", "Spoilage", "Theft", "Data Entry Error", "Supplier Short Delivery", "Damaged Goods", "Other"];
const ADJUSTMENT_REASONS = [...VARIANCE_REASONS, "Manual Correction"];

const DigitalCountPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeCount, setActiveCount] = useState(null);
  const [countHistory, setCountHistory] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activeCostTag, setActiveCostTag] = useState('all');
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [varianceReport, setVarianceReport] = useState(null);
  const [showVariance, setShowVariance] = useState(false);

  // Dialogs
  const [queryDialog, setQueryDialog] = useState({ open: false, item: null });
  const [queryNote, setQueryNote] = useState('');
  const [explainDialog, setExplainDialog] = useState({ open: false, item: null });
  const [explainReason, setExplainReason] = useState('');
  const [explainNotes, setExplainNotes] = useState('');
  const [adjustDialog, setAdjustDialog] = useState({ open: false, item: null });
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [monthEndReport, setMonthEndReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [viewCountId, setViewCountId] = useState(null);

  const isPM = user?.role === 'purchasing_manager' || user?.role === 'system_admin';
  const isSK = user?.role === 'storekeeper';
  const isFC = user?.role === 'financial_controller';

  const fetchData = useCallback(async () => {
    try {
      const [activeRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/digital-count/active`, getAuthHeaders()),
        axios.get(`${API_URL}/api/digital-count/history`, getAuthHeaders())
      ]);
      setActiveCount(activeRes.data);
      setCountHistory(historyRes.data || []);
      if (activeRes.data?.id) {
        const entriesRes = await axios.get(`${API_URL}/api/digital-count/counts/${activeRes.data.id}/entries`, getAuthHeaders());
        setEntries(entriesRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load count data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pre-count check
  const runPreCheck = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/digital-count/pre-count-check`, getAuthHeaders());
      setPreCheckResult(res.data);
      setShowPreCheck(true);
    } catch (err) {
      toast.error('Failed to run pre-count check');
    }
  };

  // Initiate count
  const initiateCount = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/digital-count/initiate`,
        { count_date: new Date().toISOString().split('T')[0] }, getAuthHeaders());
      toast.success(res.data.message);
      setShowPreCheck(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate count');
    } finally { setSubmitting(false); }
  };

  // Auto-save entry
  const saveEntry = async (itemId, value) => {
    if (!activeCount) return;
    try {
      await axios.put(`${API_URL}/api/digital-count/counts/${activeCount.id}/entry`,
        { item_id: itemId, physical_count: parseFloat(value) }, getAuthHeaders());
      setEntries(prev => prev.map(e => {
        if (e.item_id === itemId) {
          const pc = parseFloat(value);
          const variance = +(pc - e.expected_quantity).toFixed(2);
          const varPct = e.expected_quantity !== 0 ? +((variance / e.expected_quantity) * 100).toFixed(2) : 0;
          return { ...e, physical_count: pc, variance, variance_percentage: varPct };
        }
        return e;
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  // Submit draft
  const submitDraft = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/digital-count/counts/${activeCount.id}/submit-draft`, {}, getAuthHeaders());
      toast.success('Draft submitted for PM review');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  // Download Excel
  const downloadExcel = async (countId) => {
    try {
      const res = await axios.get(`${API_URL}/api/digital-count/counts/${countId || activeCount.id}/download-excel`,
        { ...getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `count_sheets.xlsx`; a.click();
      toast.success('Excel downloaded');
    } catch (err) { toast.error('Download failed'); }
  };

  // Upload Excel
  const uploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/digital-count/counts/${activeCount.id}/upload-excel`,
        formData, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.imported} items`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  // Variance report
  const loadVarianceReport = async (countId) => {
    try {
      const res = await axios.get(`${API_URL}/api/digital-count/counts/${countId || activeCount.id}/variance-report`, getAuthHeaders());
      setVarianceReport(res.data);
      setShowVariance(true);
    } catch (err) { toast.error('Failed to load report'); }
  };

  // Query item
  const submitQuery = async () => {
    if (!queryNote.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/digital-count/counts/${activeCount.id}/query-item`,
        { item_id: queryDialog.item.item_id, query_note: queryNote }, getAuthHeaders());
      toast.success('Item queried for recount');
      setQueryDialog({ open: false, item: null }); setQueryNote('');
      fetchData(); if (showVariance) loadVarianceReport();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // Explain variance
  const submitExplanation = async () => {
    if (!explainReason) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/digital-count/counts/${activeCount?.id || viewCountId}/explain-variance`,
        { item_id: explainDialog.item.item_id, reason: explainReason, notes: explainNotes }, getAuthHeaders());
      toast.success('Variance explained');
      setExplainDialog({ open: false, item: null }); setExplainReason(''); setExplainNotes('');
      if (showVariance) loadVarianceReport(viewCountId || activeCount?.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // Adjust item
  const submitAdjustment = async () => {
    if (!adjustQty || !adjustReason) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/digital-count/counts/${activeCount?.id || viewCountId}/adjust`,
        { item_id: adjustDialog.item.item_id, adjusted_quantity: parseFloat(adjustQty), reason: adjustReason, notes: adjustNotes }, getAuthHeaders());
      toast.success('Adjustment recorded');
      setAdjustDialog({ open: false, item: null }); setAdjustQty(''); setAdjustReason(''); setAdjustNotes('');
      fetchData(); if (showVariance) loadVarianceReport(viewCountId || activeCount?.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // Close count
  const closeCount = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/digital-count/counts/${activeCount?.id || viewCountId}/close`, {}, getAuthHeaders());
      toast.success('Count closed. Inventory updated.');
      setShowVariance(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close'); }
    finally { setSubmitting(false); }
  };

  // Month end report
  const loadMonthEndReport = async (countId) => {
    try {
      const res = await axios.get(`${API_URL}/api/digital-count/counts/${countId}/month-end-report`, getAuthHeaders());
      setMonthEndReport(res.data);
      setShowReport(true);
    } catch (err) { toast.error('Failed to load report'); }
  };

  // Download PDF
  const downloadPdf = async (countId, type) => {
    try {
      const res = await axios.get(`${API_URL}/api/digital-count/counts/${countId}/${type}/pdf`,
        { ...getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${type.replace('/', '_')}.pdf`; a.click();
    } catch (err) { toast.error('Download failed'); }
  };

  const filteredEntries = activeCostTag === 'all' ? entries : entries.filter(e => e.cost_tag_code === activeCostTag);
  const countedCount = entries.filter(e => e.physical_count !== null).length;
  const canEdit = activeCount && ['initiated', 'in_progress', 'queried'].includes(activeCount.status);
  const isEditable = (isSK || isPM) && canEdit;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3D1D72] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
            <ClipboardText size={24} className="text-[#3D1D72]" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Digital Count
            </h1>
            <p className="text-slate-500">Month-end inventory count workflow</p>
          </div>
        </div>
        {(isPM || isSK) && !activeCount && (
          <Button onClick={runPreCheck} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="initiate-count-btn">
            <ClipboardText size={18} className="mr-2" /> Initiate New Count
          </Button>
        )}
      </div>

      {/* Active Count */}
      {activeCount && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="active-count-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Active Count — {activeCount.count_date}</h2>
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mt-1 ${STATUS_CONFIG[activeCount.status]?.color || 'bg-slate-100'}`}
                data-testid="count-status-badge">
                {STATUS_CONFIG[activeCount.status]?.label || activeCount.status}
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(isPM || isSK) && <Button variant="outline" size="sm" onClick={() => downloadExcel()} data-testid="download-excel-btn">
                <Download size={16} className="mr-1" /> Download Excel
              </Button>}
              {isEditable && (
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" asChild data-testid="upload-excel-btn">
                    <span><Upload size={16} className="mr-1" /> Upload Excel</span>
                  </Button>
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={uploadExcel} />
                </label>
              )}
              {isPM && ['draft_submitted', 'queried'].includes(activeCount.status) && (
                <Button variant="outline" size="sm" onClick={() => loadVarianceReport()} data-testid="view-variance-btn">
                  <Eye size={16} className="mr-1" /> Variance Report
                </Button>
              )}
              {isSK && ['in_progress', 'queried'].includes(activeCount.status) && (
                <Button size="sm" onClick={submitDraft} disabled={submitting || countedCount < activeCount.total_items}
                  className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="submit-draft-btn">
                  <PaperPlaneTilt size={16} className="mr-1" /> Submit Draft
                </Button>
              )}
            </div>
          </div>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-slate-600 mb-1">
              <span>{countedCount} of {activeCount.total_items} items counted</span>
              <span>{activeCount.total_items > 0 ? Math.round(countedCount / activeCount.total_items * 100) : 0}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-[#3D1D72] h-2 rounded-full transition-all" style={{ width: `${activeCount.total_items > 0 ? (countedCount / activeCount.total_items * 100) : 0}%` }} />
            </div>
          </div>

          {/* Cost tag tabs */}
          <Tabs value={activeCostTag} onValueChange={setActiveCostTag}>
            <TabsList className="bg-slate-100 flex-wrap">
              <TabsTrigger value="all" data-testid="tab-all-items">All ({entries.length})</TabsTrigger>
              {(activeCount.cost_tags || []).map(t => (
                <TabsTrigger key={t.code} value={t.code} data-testid={`tab-${t.code}`}>
                  {t.name} ({t.item_count})
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={activeCostTag} className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="count-entries-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">SKU</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Item Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Location</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Unit</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Par</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Last Count</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Expected</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 bg-amber-50">Physical Count</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Variance</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredEntries.map(entry => (
                      <CountEntryRow key={entry.item_id} entry={entry} editable={isEditable} onSave={saveEntry}
                        isQueried={entry.query_status === 'queried'} queryNote={entry.query_note} />
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredEntries.length === 0 && (
                <div className="text-center py-12 text-slate-400"><FileText size={48} className="mx-auto mb-2" /><p>No items in this category</p></div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* No active count */}
      {!activeCount && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <ClipboardText size={64} className="mx-auto mb-4 text-slate-300" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No Active Count</h2>
          <p className="text-slate-500 mb-6">Initiate a new month-end count to begin the inventory count process.</p>
          {(isPM || isSK) && <Button onClick={runPreCheck} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="initiate-count-btn-empty">
            <ClipboardText size={18} className="mr-2" /> Initiate New Count
          </Button>}
        </div>
      )}

      {/* Count History */}
      {countHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h2 className="text-lg font-semibold text-slate-800">Count History</h2></div>
          <table className="w-full text-sm" data-testid="count-history-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Date</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Items</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Initiated By</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {countHistory.filter(c => c.status === 'closed').map(c => (
                <tr key={c.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">{c.count_date}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[c.status]?.color}`}>
                      {STATUS_CONFIG[c.status]?.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{c.total_items}</td>
                  <td className="px-4 py-3">{c.initiated_by_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {(isPM || isFC) && <>
                        <Button variant="ghost" size="sm" onClick={() => loadMonthEndReport(c.id)} data-testid={`view-report-${c.id}`}>
                          <Eye size={14} className="mr-1" /> Report
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setViewCountId(c.id); loadVarianceReport(c.id); }}>Variance</Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(c.id, 'month-end-report')}>
                          <Download size={14} className="mr-1" /> PDF
                        </Button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pre-Count Check Dialog */}
      <Dialog open={showPreCheck} onOpenChange={setShowPreCheck}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pre-Count Check</DialogTitle></DialogHeader>
          {preCheckResult && (
            <div className="space-y-4 mt-4">
              {preCheckResult.can_proceed ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="precheck-pass">
                  <div className="flex items-center gap-2 text-green-700"><CheckCircle size={20} weight="fill" /><span className="font-semibold">All clear!</span></div>
                  <p className="text-sm text-green-600 mt-1">{preCheckResult.message}</p>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="precheck-fail">
                  <div className="flex items-center gap-2 text-red-700"><Warning size={20} weight="fill" /><span className="font-semibold">Cannot proceed</span></div>
                  <p className="text-sm text-red-600 mt-1">{preCheckResult.message}</p>
                  {preCheckResult.open_requisitions?.length > 0 && (
                    <ul className="mt-3 space-y-1">{preCheckResult.open_requisitions.map((r, i) => (
                      <li key={i} className="text-sm text-red-600">• {r.requisition_number} — {r.department} ({r.status})</li>
                    ))}</ul>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPreCheck(false)}>Cancel</Button>
                {preCheckResult.can_proceed && (
                  <Button onClick={initiateCount} disabled={submitting} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="confirm-initiate-btn">
                    {submitting ? 'Initiating...' : 'Initiate Count'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Variance Report Dialog */}
      <Dialog open={showVariance} onOpenChange={setShowVariance}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Variance Report — {varianceReport?.count_date}</DialogTitle></DialogHeader>
          {varianceReport && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Total Items</p><p className="text-xl font-bold">{varianceReport.total_items}</p></div>
                <div className="bg-red-50 rounded-lg p-3"><p className="text-xs text-red-500">Items with Variance</p><p className="text-xl font-bold text-red-600">{varianceReport.items_with_variance}</p></div>
                <div className="bg-orange-50 rounded-lg p-3"><p className="text-xs text-orange-500">Unexplained</p><p className="text-xl font-bold text-orange-600">{varianceReport.unexplained_variances}</p></div>
                <div className="bg-purple-50 rounded-lg p-3"><p className="text-xs text-purple-500">Variance Value</p><p className="text-xl font-bold text-purple-600">${varianceReport.total_variance_value?.toLocaleString()}</p></div>
              </div>
              <div className="overflow-x-auto max-h-[50vh]">
                <table className="w-full text-xs" data-testid="variance-report-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">SKU</th><th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-left">Cost Centre</th>
                      <th className="px-2 py-2 text-right">Last Count</th><th className="px-2 py-2 text-right">Received</th>
                      <th className="px-2 py-2 text-right">Issued</th><th className="px-2 py-2 text-right">Expected</th>
                      <th className="px-2 py-2 text-right">Physical</th><th className="px-2 py-2 text-right">Variance</th>
                      <th className="px-2 py-2 text-right">Var%</th><th className="px-2 py-2 text-left">Explanation</th>
                      {isPM && <th className="px-2 py-2">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {varianceReport.entries?.map(e => {
                      const hasVar = e.variance && e.variance !== 0;
                      const rowClass = hasVar ? (e.variance > 0 ? 'bg-green-50' : 'bg-red-50') : '';
                      return (
                        <tr key={e.item_id} className={rowClass} data-testid={`var-row-${e.item_id}`}>
                          <td className="px-2 py-2 font-mono">{e.sku}</td>
                          <td className="px-2 py-2">{e.item_name}</td>
                          <td className="px-2 py-2">{e.cost_tag_name}</td>
                          <td className="px-2 py-2 text-right">{e.last_count_quantity}</td>
                          <td className="px-2 py-2 text-right">{e.goods_received}</td>
                          <td className="px-2 py-2 text-right">{e.requisitions_issued}</td>
                          <td className="px-2 py-2 text-right font-medium">{e.expected_quantity}</td>
                          <td className="px-2 py-2 text-right font-medium">{e.physical_count}</td>
                          <td className={`px-2 py-2 text-right font-bold ${hasVar ? (e.variance > 0 ? 'text-green-700' : 'text-red-700') : ''}`}>
                            {e.variance !== null ? (e.variance > 0 ? '+' : '') + e.variance : '-'}
                          </td>
                          <td className={`px-2 py-2 text-right ${hasVar ? (e.variance > 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                            {e.variance_percentage !== null ? e.variance_percentage + '%' : '-'}
                          </td>
                          <td className="px-2 py-2">{e.variance_reason ? <span className="text-green-700">{e.variance_reason}</span> : (hasVar ? <span className="text-red-500 font-medium">Unexplained</span> : '-')}</td>
                          {isPM && <td className="px-2 py-2">
                            <div className="flex gap-1">
                              {hasVar && !e.variance_reason && (
                                <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => { setExplainDialog({ open: true, item: e }); setExplainReason(''); setExplainNotes(''); }}
                                  data-testid={`explain-btn-${e.item_id}`}><Pencil size={12} /></Button>
                              )}
                              {varianceReport.status !== 'closed' && <>
                                <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => { setQueryDialog({ open: true, item: e }); setQueryNote(''); }}
                                  data-testid={`query-btn-${e.item_id}`}><MagnifyingGlass size={12} /></Button>
                                <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => { setAdjustDialog({ open: true, item: e }); setAdjustQty(String(e.physical_count || '')); setAdjustReason(''); setAdjustNotes(''); }}
                                  data-testid={`adjust-btn-${e.item_id}`}><ArrowClockwise size={12} /></Button>
                              </>}
                            </div>
                          </td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(varianceReport.count_id, 'variance-report')}>
                    <Download size={14} className="mr-1" /> Variance PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(varianceReport.count_id, 'adjustment-log')}>
                    <Download size={14} className="mr-1" /> Adjustment Log PDF
                  </Button>
                </div>
                {isPM && varianceReport.can_close && (
                  <Button onClick={closeCount} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="close-count-btn">
                    <Lock size={16} className="mr-1" /> {submitting ? 'Closing...' : 'Close Count'}
                  </Button>
                )}
                {isPM && !varianceReport.can_close && varianceReport.unexplained_variances > 0 && varianceReport.status !== 'closed' && (
                  <p className="text-sm text-red-500 font-medium" data-testid="unexplained-warning">
                    {varianceReport.unexplained_variances} unexplained variance(s) — explain all before closing
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Month End Report Dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Month End Inventory Report — {monthEndReport?.count_date}</DialogTitle></DialogHeader>
          {monthEndReport && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-50 rounded-lg p-3"><p className="text-xs text-purple-500">Total Items</p><p className="text-xl font-bold">{monthEndReport.total_items}</p></div>
                <div className="bg-green-50 rounded-lg p-3"><p className="text-xs text-green-500">Total Closing Value</p><p className="text-xl font-bold text-green-600">${monthEndReport.total_closing_value?.toLocaleString()}</p></div>
              </div>
              <div className="overflow-x-auto max-h-[50vh]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">SKU</th><th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-left">Cost Centre</th>
                      <th className="px-2 py-2 text-right">Opening</th><th className="px-2 py-2 text-right">Received</th>
                      <th className="px-2 py-2 text-right">Issued</th><th className="px-2 py-2 text-right">Expected</th>
                      <th className="px-2 py-2 text-right">Physical</th><th className="px-2 py-2 text-right">Variance</th>
                      <th className="px-2 py-2 text-left">Reason</th><th className="px-2 py-2 text-right">WAC</th>
                      <th className="px-2 py-2 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {monthEndReport.items?.map((item, i) => (
                      <tr key={i} className={item.variance !== 0 ? (item.variance > 0 ? 'bg-green-50' : 'bg-red-50') : ''}>
                        <td className="px-2 py-2 font-mono">{item.sku}</td>
                        <td className="px-2 py-2">{item.item_name}</td>
                        <td className="px-2 py-2">{item.cost_tag}</td>
                        <td className="px-2 py-2 text-right">{item.opening_stock}</td>
                        <td className="px-2 py-2 text-right">{item.goods_received}</td>
                        <td className="px-2 py-2 text-right">{item.requisitions_issued}</td>
                        <td className="px-2 py-2 text-right">{item.expected_closing}</td>
                        <td className="px-2 py-2 text-right font-medium">{item.physical_count}</td>
                        <td className={`px-2 py-2 text-right font-bold ${item.variance > 0 ? 'text-green-700' : item.variance < 0 ? 'text-red-700' : ''}`}>
                          {item.variance > 0 ? '+' : ''}{item.variance}
                        </td>
                        <td className="px-2 py-2">{item.variance_reason || '-'}</td>
                        <td className="px-2 py-2 text-right">${item.wac?.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-medium">${item.closing_value?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => downloadPdf(monthEndReport.count_id, 'month-end-report')}>
                  <Download size={14} className="mr-1" /> Month End PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(monthEndReport.count_id, 'variance-report')}>
                  <Download size={14} className="mr-1" /> Variance PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(monthEndReport.count_id, 'adjustment-log')}>
                  <Download size={14} className="mr-1" /> Adjustment Log PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Query Dialog */}
      <Dialog open={queryDialog.open} onOpenChange={(o) => setQueryDialog({ ...queryDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Query Item — {queryDialog.item?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p>Expected: <strong>{queryDialog.item?.expected_quantity}</strong> | Counted: <strong>{queryDialog.item?.physical_count}</strong> | Variance: <strong>{queryDialog.item?.variance}</strong></p>
            </div>
            <div className="space-y-2">
              <Label>Query Note <span className="text-red-500">*</span></Label>
              <Textarea value={queryNote} onChange={e => setQueryNote(e.target.value)} placeholder="What needs to be recounted and why..."
                data-testid="query-note-input" rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setQueryDialog({ open: false, item: null })}>Cancel</Button>
              <Button onClick={submitQuery} disabled={!queryNote.trim() || submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-query-btn">
                Send Query
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Explain Variance Dialog */}
      <Dialog open={explainDialog.open} onOpenChange={(o) => setExplainDialog({ ...explainDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Explain Variance — {explainDialog.item?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p>Expected: <strong>{explainDialog.item?.expected_quantity}</strong> | Counted: <strong>{explainDialog.item?.physical_count}</strong></p>
              <p className={`font-bold ${explainDialog.item?.variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                Variance: {explainDialog.item?.variance > 0 ? '+' : ''}{explainDialog.item?.variance}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Select value={explainReason} onValueChange={setExplainReason}>
                <SelectTrigger data-testid="explain-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{VARIANCE_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={explainNotes} onChange={e => setExplainNotes(e.target.value)} placeholder="Additional details..." data-testid="explain-notes-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setExplainDialog({ open: false, item: null })}>Cancel</Button>
              <Button onClick={submitExplanation} disabled={!explainReason || submitting} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="submit-explain-btn">
                Save Explanation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog open={adjustDialog.open} onOpenChange={(o) => setAdjustDialog({ ...adjustDialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust — {adjustDialog.item?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Adjusted Quantity <span className="text-red-500">*</span></Label>
              <Input type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} data-testid="adjust-qty-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Select value={adjustReason} onValueChange={setAdjustReason}>
                <SelectTrigger data-testid="adjust-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{ADJUSTMENT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)} data-testid="adjust-notes-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdjustDialog({ open: false, item: null })}>Cancel</Button>
              <Button onClick={submitAdjustment} disabled={!adjustQty || !adjustReason || submitting} className="bg-[#3D1D72] hover:bg-[#2D1652]" data-testid="submit-adjust-btn">
                Record Adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Individual count entry row with inline editing
const CountEntryRow = ({ entry, editable, onSave, isQueried, queryNote }) => {
  const [value, setValue] = useState(entry.physical_count !== null ? String(entry.physical_count) : '');
  const [saving, setSaving] = useState(false);

  const handleBlur = async () => {
    if (value === '' || value === String(entry.physical_count)) return;
    setSaving(true);
    await onSave(entry.item_id, value);
    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  const hasVariance = entry.variance !== null && entry.variance !== 0;

  return (
    <tr className={`${isQueried ? 'bg-orange-50' : ''} ${hasVariance ? (entry.variance > 0 ? 'bg-green-50/30' : 'bg-red-50/30') : ''}`}
      data-testid={`entry-row-${entry.item_id}`}>
      <td className="px-3 py-2 font-mono text-xs text-slate-500">{entry.sku}</td>
      <td className="px-3 py-2">
        <span className="font-medium text-slate-800">{entry.item_name}</span>
        {isQueried && <p className="text-xs text-orange-600 mt-0.5" data-testid={`query-note-${entry.item_id}`}>Recount: {queryNote}</p>}
        {entry.variance_reason && <p className="text-xs text-green-600 mt-0.5">Explained: {entry.variance_reason}</p>}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">{entry.location_name}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{entry.unit_of_measure}</td>
      <td className="px-3 py-2 text-right text-slate-600">{entry.par_level}</td>
      <td className="px-3 py-2 text-right text-slate-600">{entry.last_count_quantity}</td>
      <td className="px-3 py-2 text-right font-medium text-slate-700">{entry.expected_quantity}</td>
      <td className="px-3 py-2 text-right bg-amber-50/50">
        {editable ? (
          <Input type="number" min="0" step="0.01" value={value} onChange={e => setValue(e.target.value)}
            onBlur={handleBlur} onKeyDown={handleKeyDown}
            className={`h-7 w-20 text-right text-sm ml-auto ${saving ? 'opacity-50' : ''}`}
            data-testid={`count-input-${entry.item_id}`} />
        ) : (
          <span className="font-medium">{entry.physical_count !== null ? entry.physical_count : '-'}</span>
        )}
      </td>
      <td className={`px-3 py-2 text-right font-bold ${hasVariance ? (entry.variance > 0 ? 'text-green-700' : 'text-red-700') : 'text-slate-400'}`}>
        {entry.variance !== null ? (entry.variance > 0 ? '+' : '') + entry.variance : '-'}
      </td>
      <td className="px-3 py-2">
        {isQueried && <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Queried</span>}
        {entry.query_status === 'recounted' && <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Recounted</span>}
        {entry.physical_count !== null && !isQueried && !entry.query_status && <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">Counted</span>}
      </td>
    </tr>
  );
};

export default DigitalCountPage;
